import { Route, Routes } from '@angular/router';
// Eagerly import all components for offline support (no lazy loading)
import { LoginComponent } from './login/login.component';
import { SsoAuthComponent } from './sso-auth/sso-auth.component';
import { ProfileComponent } from './profile/profile.component';
import { GrowersComponent } from './growers/growers.component';
import { TransloadingComponent } from './transloading/transloading.component';
import { SchedulerComponent } from './scheduler/scheduler.component';
import { GatehouseComponent } from './gatehouse/gatehouse.component';
import { ReceiverComponent } from './receiver/receiver.component';
import { ShuntingComponent } from './shunting/shunting.component';
import { AuditComponent } from './audit/audit.component';
import { PassportDetailsPage } from './passport-details/passport-details.page';
import { CommentsComponent } from './comments/comments.component';
import { CommentsNotificationsComponent } from './comments/comments-notifications.component';
import { HistoryComponent } from './history/history.component';
import { ContactsComponent } from './contacts/contacts.component';
import { AdminComponent } from './admin/admin.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { MaintenancePageComponent } from './maintenance/maintenance.page';
import { authGuard } from './core/guards/auth.guard';
import { personaGuard } from './core/guards/persona.guard';
import { maintenanceGuard } from './core/guards/maintenance.guard';

const rawRoutes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'login' },
  { path: 'login', component: LoginComponent },
  { path: 'sso', component: SsoAuthComponent },
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: 'maintenance', component: MaintenancePageComponent },

  // Public routes that require auth but not persona check
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [authGuard],
  },
  {
    path: 'contacts',
    component: ContactsComponent,
    canActivate: [authGuard],
  },

  // Admin route - requires both auth and admin persona
  {
    path: 'admin',
    component: AdminComponent,
    canActivate: [authGuard, personaGuard],
  },

  // Persona-specific routes - require both auth and persona check
  {
    path: 'growers',
    component: GrowersComponent,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'transloading',
    component: TransloadingComponent,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'receiver',
    component: ReceiverComponent,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'shunting',
    component: ShuntingComponent,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'scheduler',
    component: SchedulerComponent,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'gatehouse',
    component: GatehouseComponent,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'audit',
    component: AuditComponent,
    canActivate: [authGuard, personaGuard],
  },

  // Other routes that require auth
  {
    path: 'passport/:id',
    component: PassportDetailsPage,
    canActivate: [authGuard, personaGuard],
  },
  {
    path: 'comments',
    component: CommentsComponent,
    canActivate: [authGuard],
  },
  {
    path: 'comments-notifications',
    component: CommentsNotificationsComponent,
    canActivate: [authGuard],
  },
  {
    path: 'history',
    component: HistoryComponent,
    canActivate: [authGuard],
  },

  { path: '**', redirectTo: 'login' },
];

function withMaintenanceGuard(route: Route): Route {
  if (route.path === 'maintenance') {
    return route;
  }
  if (route.redirectTo !== undefined) {
    // Redirects cannot be guarded; ensure any guards are applied to the target route.
    const { canActivate, ...rest } = route;
    return rest;
  }
  const existing = route.canActivate ?? [];
  return { ...route, canActivate: [maintenanceGuard, ...existing] };
}

export const routes: Routes = rawRoutes.map(withMaintenanceGuard);
